import { Typography } from "@mui/material";
import type { AutocompleteInputChangeReason } from "@mui/material/Autocomplete";
import type { AutocompleteCloseReason } from "@mui/material/useAutocomplete";
import type { FC, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { INK } from "@/theme/tokens";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "@/ui/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { SwitcherRow } from "@/utils/facilityDepartments";
import { SwitcherRowLabel } from "./SwitcherRowLabel";

const LISTBOX_MAX_HEIGHT = 480;

export type SwitcherRowOption = SystemAutoCompleteOption<SwitcherRow>;

// The rows arrive already searched: `buildSwitcherRows` filters them and decides what a search expands.
const keepRows = (rows: SwitcherRowOption[]): SwitcherRowOption[] => rows;

type Props = {
	options: SwitcherRowOption[];
	selectedValue: SwitcherRowOption | null;
	/** Set when the rows span several networks, to head each network's facilities with its name. */
	groupBy: ((option: SwitcherRowOption) => string) | undefined;
	chosenFacilityId: string | undefined;
	query: string;
	onInputChange: (event: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
	onChange: (event: SyntheticEvent, option: SwitcherRowOption | null) => void;
	onOpenStateChange: (event: SyntheticEvent, reason: AutocompleteCloseReason | undefined, isOpen: boolean) => void;
};

/** The header switcher's always-open search list of facilities and departments. */
export const DepartmentSwitcherList: FC<Props> = ({ options, selectedValue, groupBy, chosenFacilityId, query, onInputChange, onChange, onOpenStateChange }) => {
	const { t } = useTranslation();
	return (
		<SystemAutoComplete<SwitcherRow>
			open
			options={options}
			value={selectedValue}
			onChange={onChange}
			onOpenStateChange={onOpenStateChange}
			inputValue={query}
			onInputChange={onInputChange}
			filterOptions={keepRows}
			groupBy={groupBy}
			dimUnselectedOptions={false}
			fullWidth
			dataTestId="header-departments-items"
			getOptionDataTestId={({ value: row }) =>
				row.kind === "facility" ? `facility-item-${row.group.facility.facility_id}` : `department-item-${row.department.department_id}`
			}
			renderOptionLabel={({ value: row }, { isSelected }) => (
				<SwitcherRowLabel
					row={row}
					isEmphasized={isSelected || (row.kind === "facility" && !row.isExpanded && row.group.facility.facility_id === chosenFacilityId)}
				/>
			)}
			getOptionEndAdornment={({ value: row }) =>
				row.kind === "facility" ? (
					<Typography variant="caption" sx={{ color: INK.MUTED }}>
						{t("header.facilitySwitcher.departmentCount", { count: row.group.departments.length })}
					</Typography>
				) : null
			}
			optionEndAdornmentSelectsOption
			textFieldProps={{
				placeholder: t("header.facilitySwitcher.search"),
				reserveHeightForHelperText: false,
				formControlSx: { paddingBottom: "5px", padding: 1 },
			}}
			slotProps={{
				popupIndicator: {
					sx: { display: "none" },
				},
				popper: {
					disablePortal: true,
					sx: { width: "100% !important", position: "static !important", transform: "none !important" },
				},
				paper: {
					sx: { boxShadow: "none", borderRadius: 0, mt: 0 },
				},
				listbox: {
					sx: {
						maxHeight: LISTBOX_MAX_HEIGHT,
						"& .MuiAutocomplete-option": { paddingX: 1 },
						"& .MuiAutocomplete-groupLabel": { fontWeight: "bold", paddingY: 0 },
					},
				},
			}}
		/>
	);
};
